import React, { useEffect } from 'react'
import { useNavigation } from '@react-navigation/native'
import { Divider } from '@ui-kitten/components'
import { useDispatch, useSelector } from 'react-redux'
import { StackNavigationProp } from '@react-navigation/stack'
import { omit } from 'lodash'
import { BillingProduct } from '@src/types'
import { CheckoutContainer } from '@src/screens/Marketplace/components/checkout/CheckoutContainer'
import { ProductDescription } from '@src/screens/Marketplace/components/checkout/ProductDescription'
import { ProductPayment } from '@src/screens/Marketplace/components/checkout/ProductPayment'
import {
  usePromotionCode,
  usePurchaseSubscription,
  useTaxEstimation,
} from '@src/screens/Marketplace/utils/hooks'
import {
  monthlyNutritionCoachingProductSelector,
  paymentMethodSelector,
  userSelector,
} from '@src/selectors/app'
import { RootStackParamList } from '@src/navigation/types'
import { useDispatchAsync } from '@src/utils'
import { useSecureScreen } from '@src/hooks/useSecureScreen'

export const MonthlyNutritionCoachingCheckoutScreen = () => {
  useSecureScreen()

  const purchaseSubscription = usePurchaseSubscription()
  const navigation = useNavigation<StackNavigationProp<RootStackParamList>>()
  const dispatch = useDispatch()
  const dispatchAsync = useDispatchAsync()
  const user = useSelector(userSelector)
  const address = user?.address

  const paymentMethod = useSelector(paymentMethodSelector)
  const product = useSelector(monthlyNutritionCoachingProductSelector)

  const productKey = product?.key as BillingProduct

  const onCheckoutPress = async () => {
    const subscription = await purchaseSubscription({
      paymentMethodId: paymentMethod?.stripeId,
      productKey,
      promotionCode,
      address: omit(address, ['__typename']),
    })

    if (!subscription) {
      return
    }

    dispatch({ type: 'uiFlags/fetch' })

    try {
      await dispatchAsync({ type: 'users/fetch' })
    } catch {
      // do nothing
    }

    navigation.reset({
      index: 2,
      routes: [
        {
          name: 'Drawer',
          state: { routes: [{ name: 'Dashboard' }] },
        },
        { name: 'Account' },
        {
          name: 'Subscriptions',
        },
      ],
    })
  }

  const promotionCode = usePromotionCode(productKey)

  const { estimatedTax } = useTaxEstimation({
    productKey,
    promotionCode,
    zipCode: address?.zipCode,
    country: address?.country,
  })

  useEffect(() => {
    if (!product) {
      navigation.replace('Drawer', { screen: 'Dashboard', params: { screen: 'NutritionistHub' } })
    }
  }, [navigation, product])

  if (!product) {
    return null
  }

  const productTitle = product.title
  const productDescription = product.description ?? 'Billed monthly, auto renewal month-to-month'

  return (
    <CheckoutContainer checkoutButtonTitle="Purchase Now" onCheckout={onCheckoutPress}>
      <ProductDescription
        title={productTitle}
        description={productDescription}
        price={`$${product.price}`}
      />

      <ProductPayment
        product={product.key as BillingProduct}
        fullPrice={product.price}
        estimatedTax={estimatedTax}
        promotionCode={promotionCode}
      />

      <Divider appearance="large" />
    </CheckoutContainer>
  )
}
